import '../styles/chart-landscape-fullscreen.css';
import {ChartLibraryPanel} from '../components/charts/ChartLibraryPanel';
import {orderDashboardItems, libraryVariantMatches, type DashboardLibraryItem} from '../utils/dashboardChartOrder';
import { AthleteSwitcher } from '../components/AthleteSwitcher';
import { AppHeading } from '../components/AppTypography';
import { ensureDefaultMetricsChart } from '../components/charts/ensureDefaultMetricsChart';
import { ChartPmcSplit } from '../components/charts/ChartPmcSplit';
import { primaryActionStyle } from '../components/primaryActionStyle';
import { displayDigits } from '../utils/displayDigits';
import { chartToolbarButtonStyle } from '../components/charts/chartToolbarButtonStyle';
import { ChartRunningProfile } from '../components/charts/ChartRunningProfile';
import { chartIdentity, uniqueCharts } from '../utils/chartIdentity';
import { SportDurationDonut } from '../components/charts/SportDurationDonut';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import axios from 'axios';
import { NmEmptyState } from '../components/NmEmptyState';
import { ChartRangePicker } from '../features/charts/ChartRangePicker';
import { NmLoadingOverlay } from '../components/NmLoadingOverlay';
import { AthleteMetricsBar } from '../components/AthleteMetricsBar';
import { WorkoutDetailPanel } from './WorkoutDetailPanel';
import { DashboardPeakTrends } from '../components/coach/DashboardPeakTrends';
import type { UserProfile, UserChartEntry, AvailableChart, AthleteItem } from '../services/api';
import { chartManager } from '../services/api';
import { usePageRefresh } from '../context/PageRefreshContext';
import { useToast } from '../context/ToastContext';
import { formatIsoDateLabel } from '../i18n/formatters';
import {
  DEFAULT_PERFORMANCE_INSIGHTS,
  PERFORMANCE_INSIGHT_DEFS,
  PerformanceInsightWidgets,
  OPTIONAL_PERFORMANCE_INSIGHTS,
  SUPPORTED_PERFORMANCE_INSIGHTS,
  type PerformanceInsightType,
} from '../components/charts/PerformanceInsightWidgets';
import '../styles/charts-workspace.css';
import {
  ChartPeriodic, ChartDonut, ChartBars, ChartHrZones, ChartStackedZones, ChartPowerProfile, ChartMetricsTrend,
  PmcSettingsPanel,
  MetricsChartSettingsPanel,
  ChartVariantSettingsPanel,
  normDonut, normBars, normPeriodicChart, normPowerProfile, normMetricsChart,
  normZonesByWeek, deriveHrZones,
  mergePmcSettings, pmcReportToPeriodicData, mergePeakData,
  type PmcChartSettings,
  type MetricsChartSettings,
  BLUE, BLUEL, PINKL, GREEN, GOLD,
} from '../components/charts';
import { ChartGuideButton } from '../components/charts/ChartGuideButton';

// ─── Local types ──────────────────────────────────────────────────────────────
type Range = 7 | 14 | 30 | 90 | 180 | 365 | 730 | -1; // -1 = custom range

// ─── Helpers ──────────────────────────────────────────────────────────────────
function LegDot({ color, circle }: { color: string; circle?: boolean }) {
  return (
    <span style={{ display: 'inline-block', width: circle ? 7 : 10, height: circle ? 7 : 4, borderRadius: 999, background: color, flexShrink: 0 }} />
  );
}


interface ChartRenderCtx {
  data: unknown;
  range: Range;
  setting: Record<string, unknown>;
  periodicData?: unknown;
  dateRange?: { start: string; end: string };
  fullscreen?: boolean;
  embedded?: boolean;
  onWorkoutClick?: (workoutId: number) => void;
  onOpenSettings?: () => void;
}

// ─── CHART_DEFS registry ──────────────────────────────────────────────────────
interface ChartDef {
  eye: string;
  title: string;
  category: string;
  description: string;
  render: (ctx: ChartRenderCtx) => React.ReactNode;
  legend?: (t: (key: string) => string) => React.ReactNode;
  hasSettings?: boolean;
  settingsType?: 'pmc' | 'metrics' | 'variant';
  supportsFullscreen?: boolean;
}

const CHART_DEFS: Record<string, ChartDef> = {
  pmc_report: {
    eye: 'Performance Management',
    title: 'Fitness · Fatigue · Form',
    category: 'Load',
    description: 'CTL, ATL and TSB training load over time',
    hasSettings: true,
    supportsFullscreen: true,
    render: ({ data, range, setting, periodicData, fullscreen, onWorkoutClick, dateRange }) => {
      const mergedSettings = mergePmcSettings(setting);
      let periodic = pmcReportToPeriodicData(data, range === -1 ? 730 : range);
      periodic = mergePeakData(periodic, periodicData, mergedSettings);
      return (
        <ChartPmcSplit
          data={periodic}
          settings={mergedSettings}
          dateRange={dateRange}
          fullscreen={fullscreen}
          onWorkoutClick={onWorkoutClick}
        />
      );
    },
  },
  fitness_summery_chart: {
    eye: 'Fitness Summary',
    title: 'Sport Breakdown',
    category: 'Summary',
    description: 'Sport breakdown by selected metric (distance, duration, TSS, calories)',
    render: ({ data, setting }) => (
      <ChartDonut data={normDonut(data, String(setting.fitness_type ?? ''))} />
    ),
  },
  fitness_summery_report: {
    eye: 'Fitness Report',
    title: 'Training Overview',
    category: 'Summary',
    description: 'Cumulative fitness metrics and totals',
    render: ({ data }) => {
      const bars = normBars(data);
      return <SportDurationDonut data={bars} />;
    },
  },
  nutrition_chart: {
    eye: 'Calories',
    title: 'Calories by Week',
    category: 'Weekly',
    description: 'Weekly calorie expenditure trend',
    render: ({ data }) => <ChartBars data={normBars(data)} color={GREEN} unit="kcal" />,
  },
  periodic_chart: {
    eye: 'Training Report',
    title: 'Periodic Chart',
    category: 'Reports',
    description: 'Show any training metric by day, week or month',
    hasSettings: true,
    settingsType: 'variant',
    render: ({ data, setting }) => {
      const ws = setting.workout_summery as Record<string, unknown> | undefined;
      const metric = String(ws?.workout_summery_types ?? 'tss_actual');
      const groupBy = String(ws?.group_by ?? 'week');
      const isMonthlyHours = metric === 'total_time' && groupBy === 'month';
      const isTime = metric === 'total_time';
      const bars = normPeriodicChart(data, metric);
      return (
        <>
          <ChartBars data={bars} color={isMonthlyHours ? BLUEL : GOLD} unit={isTime ? 'time' : metric === 'distance' ? 'km' : 'number'} flush tight={isMonthlyHours} interactive height={240} fillHeight />

        </>
      );
    },
  },
  running_profile: {
    eye: 'Running Performance',
    title: 'Running Performance Profile',
    category: 'Performance',
    description: 'Best running efforts, population percentiles and historical progress',
    supportsFullscreen: true,
    render: ({ data, onWorkoutClick }) => <ChartRunningProfile data={data} onWorkoutClick={onWorkoutClick} />,
  },
  power_profile: {
    eye: 'Power Profile',
    title: 'Power Profile',
    category: 'Performance',
    description: 'Normalized 5s / 1min / 5min / 20min peak power vs athlete categories',
    hasSettings: true,
    settingsType: 'variant',
    render: ({ data, onWorkoutClick, onOpenSettings, embedded, fullscreen }) => (
      <ChartPowerProfile
        data={normPowerProfile(data)}
        embedded={embedded}
        height={fullscreen ? 360 : embedded ? 300 : 280}
        onWorkoutClick={onWorkoutClick}
        onSettingsPress={onOpenSettings}
      />
    ),
  },
  time_in_zone: {
    eye: 'Time in Zones',
    title: 'Zone Distribution by Week',
    category: 'Zones',
    description: 'Weekly time distribution by training zone',
    hasSettings: true,
    settingsType: 'variant',
    render: ({ data }) => {
      const zones = normZonesByWeek(data);
      return zones.length
        ? <ChartStackedZones data={zones} flush interactive height={240} />
        : <ChartHrZones zones={deriveHrZones(zones)} flush />;
    },
  },
  metrics_chart: {
    eye: 'Body Metrics',
    title: 'Metrics Trend',
    category: 'Body',
    description: 'Track calendar metrics such as weight over time',
    hasSettings: true,
    settingsType: 'metrics',
    render: ({ data }) => (
      <ChartMetricsTrend series={normMetricsChart(data)} flush height={240} />
    ),
  },
};

/** Per-metric labels for fitness_summery_chart — same chart_type, different fitness_type in setting */
const FITNESS_SUMMARY_LABELS: Record<string, { eye: string; title: string }> = {
  distance:         { eye: 'Sport Breakdown',   title: 'Distance by Sport' },
  total_time:       { eye: 'Training Duration', title: 'Duration by Sport' },
  tss_actual:       { eye: 'Training Load',     title: 'TSS by Sport' },
  calories:         { eye: 'Energy',            title: 'Calories by Sport' },
  distance_planned: { eye: 'Planned Volume',    title: 'Planned Distance by Sport' },
  tss_planned:      { eye: 'Planned Load',      title: 'Planned TSS by Sport' },
  calories_planned: { eye: 'Planned Energy',    title: 'Planned Calories by Sport' },
  planned_duration: { eye: 'Planned Duration',  title: 'Planned Duration by Sport' },
  if_actual:        { eye: 'Intensity',         title: 'Intensity Factor by Sport' },
  total_ascent:     { eye: 'Elevation',         title: 'Elevation Gain by Sport' },
  total_descent:    { eye: 'Elevation',         title: 'Elevation Loss by Sport' },
  work:             { eye: 'Work',              title: 'Work (kJ) by Sport' },
};

/** Per-metric labels for time_in_zone — same chart_type, different zone_type in setting */
const TIME_IN_ZONE_LABELS: Record<string, { eye: string; title: string }> = {
  time_in_zone_hr: { eye: 'Heart Rate Zones', title: 'HR Zone Distribution by Week' },
  time_in_zone_pw: { eye: 'Power Zones',      title: 'Power Zone Distribution by Week' },
  time_in_zone_sd: { eye: 'Pace Zones',       title: 'Pace Zone Distribution by Week' },
  time_in_zone_cd: { eye: 'Cadence Zones',    title: 'Cadence Zone Distribution by Week' },
};

function resolveChartDisplay(entry: UserChartEntry, def: ChartDef | undefined, t: TFunction): {eye:string;title:string} {
  const display = resolveBaseChartDisplay(entry, def, t);
  if (['periodic_chart','time_in_zone'].includes(entry.chart_type)) {
    const sports = Array.isArray(entry.setting?.workout_types) ? entry.setting.workout_types.map(Number) : [];
    if (sports.length && !sports.includes(0)) display.title += ' · ' + sports.map(id => t('charts.library.sports.'+id, {defaultValue:String(id)})).join('، ');
  }
  return display;
}
function resolveBaseChartDisplay(
  entry: UserChartEntry,
  def: ChartDef | undefined,
  t: TFunction,
): { eye: string; title: string } {
  if (entry.chart_type === 'fitness_summery_chart') {
    const ft = String(entry.setting?.fitness_type ?? 'distance');
    const fallback = FITNESS_SUMMARY_LABELS[ft];
    return {
      eye: t(`charts.defs.fitness.${ft}.eye`, { defaultValue: fallback?.eye ?? def?.eye ?? 'Fitness Summary' }),
      title: t(`charts.defs.fitness.${ft}.title`, { defaultValue: fallback?.title ?? def?.title ?? 'Sport Breakdown' }),
    };
  }

  if (entry.chart_type === 'time_in_zone') {
    const zt = String(entry.setting?.zone_type ?? 'time_in_zone_hr');
    const fallback = TIME_IN_ZONE_LABELS[zt];
    return {
      eye: t(`charts.defs.zones.${zt}.eye`, { defaultValue: fallback?.eye ?? def?.eye ?? 'Time in Zones' }),
      title: t(`charts.defs.zones.${zt}.title`, { defaultValue: fallback?.title ?? def?.title ?? 'Zone Distribution by Week' }),
    };
  }

  if (entry.chart_type === 'periodic_chart') {
    const ws = entry.setting?.workout_summery as Record<string, unknown> | undefined;
    const metric = String(ws?.workout_summery_types ?? 'tss_actual');
    const groupBy = String(ws?.group_by ?? 'week');
    const metricLabel = t(`charts.defs.periodic.metric.${metric}`, {
      defaultValue: metric === 'tss_actual' ? 'TSS' : metric,
    });
    const groupLabel = t(`charts.defs.periodic.group.${groupBy}`, {
      defaultValue: groupBy === 'month' ? 'Monthly' : groupBy === 'day' ? 'Daily' : 'Weekly',
    });
    return {
      eye: t('charts.defs.periodic_chart.eye', { defaultValue: def?.eye ?? 'Training Load' }),
      title: t('charts.defs.periodic_chart.titleWithMeta', {
        metric: metricLabel,
        group: groupLabel,
        defaultValue: `${metricLabel} · ${groupLabel}`,
      }),
    };
  }

  const typeKey = entry.chart_type;
  return {
    eye: typeKey === 'power_profile' ? t('charts.library.types.power_profile.title') : t(`charts.defs.${typeKey}.eye`, { defaultValue: def?.eye ?? typeKey }),
    title: ['pmc_report','power_profile','running_profile','fitness_summery_report','readiness','metrics_chart','nutrition_chart'].includes(typeKey) ? t(`charts.library.types.${typeKey}.title`) : t(`charts.defs.${typeKey}.title`, { defaultValue: def?.title ?? 'Chart' }),
  };
}

// ─── ChartInstance ─────────────────────────────────────────────────────────────
function ChartInstance({
  entry, range, index, dragFrom, dropTarget,
  periodicData,
  userId,
  onRemove, onDragStart, onDragEnter, onDrop,   onDragEnd,
  onSettingSaved,
  onWorkoutClick,
  onScrollToRange,
  recalcDateRange,
  onChartsRefetch,
  onOpenAthleteSettings,
  mobile,
  librarySettingsRequest,
}: {
  entry: UserChartEntry;
  range: Range;
  index: number;
  dragFrom: number | null;
  dropTarget: number | null;
  periodicData?: unknown;
  userId: number;
  onRemove: () => void;
  onDragStart: (i: number) => void;
  onDragEnter: (i: number) => void;
  onDrop: (to: number) => void;
  onDragEnd: () => void;
  onSettingSaved: (chartId: number, setting: Record<string, unknown>, chartType: string) => Promise<void>;
  onWorkoutClick?: (workoutId: number) => void;
  onScrollToRange?: () => void;
  recalcDateRange: { start: string; end: string };
  onChartsRefetch: () => Promise<void>;
  onOpenAthleteSettings?: (userId: number) => void;
  mobile?: boolean;
  librarySettingsRequest?: {chartId:number;token:number}|null;
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const def = CHART_DEFS[entry.chart_type];
  const display = resolveChartDisplay(entry, def, t);
  const periodCaption = t('charts.card.periodRange', {
    start: formatIsoDateLabel(recalcDateRange.start),
    end: formatIsoDateLabel(recalcDateRange.end),
    defaultValue: `${formatIsoDateLabel(recalcDateRange.start)} – ${formatIsoDateLabel(recalcDateRange.end)}`,
  });
  const isRtl = i18n.dir() === 'rtl';
  const isDragging  = dragFrom === index;
  const isDropTarget = dropTarget === index && dragFrom !== null && dragFrom !== index;
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(()=>{if(librarySettingsRequest?.chartId===entry.chart_id)setSettingsOpen(true);},[librarySettingsRequest?.chartId,librarySettingsRequest?.token,entry.chart_id]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const ownsBrowserFullscreenRef = useRef(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const openFullscreen = useCallback(() => {
    setFullscreen(true);

    void (async () => {
      try {
        if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
          ownsBrowserFullscreenRef.current = true;
        }
      } catch {
        // Keep the in-app fullscreen overlay when native fullscreen is unavailable.
      }

      try {
        const orientation = window.screen.orientation as ScreenOrientation & {
          lock?: (mode: 'landscape') => Promise<void>;
        };
        await orientation.lock?.('landscape');
      } catch {
        // Orientation lock is not available on every browser (notably iOS Safari).
      }
    })();
  }, []);

  const closeFullscreen = useCallback(() => {
    setFullscreen(false);
  }, []);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullscreen();
    };
    const onFullscreenChange = () => {
      if (ownsBrowserFullscreenRef.current && !document.fullscreenElement) {
        ownsBrowserFullscreenRef.current = false;
        closeFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      window.screen.orientation?.unlock?.();
      if (ownsBrowserFullscreenRef.current && document.fullscreenElement) {
        ownsBrowserFullscreenRef.current = false;
        void document.exitFullscreen().catch(() => undefined);
      }
    };
  }, [closeFullscreen, fullscreen]);

  const chartCtx: ChartRenderCtx = {
    data: entry.data,
    dateRange: recalcDateRange,
    range,
    setting: entry.setting ?? {},
    periodicData,
    onWorkoutClick,
    embedded: true,
    onOpenSettings: entry.chart_type === 'power_profile'
      ? () => setSettingsOpen(true)
      : () => setSettingsOpen(true),
  };

  async function handleSaveSettings(nextSetting: Record<string, unknown>) {
    setSavingSettings(true);
    try {
      await onSettingSaved(entry.chart_id, nextSetting, entry.chart_type);
      setSettingsOpen(false);
    } catch (e) {
      showToast(e instanceof Error ? e.message : t('toast.failed'));
    } finally {
      setSavingSettings(false);
    }
  }

  return (
    <>
      <div
        className="charts-report-card"
        draggable={!mobile}
        onDragStart={mobile ? undefined : (e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(index); }}
        onDragEnter={mobile ? undefined : () => { if (dragFrom !== null) onDragEnter(index); }}
        onDragOver={mobile ? undefined : (e) => e.preventDefault()}
        onDrop={mobile ? undefined : () => onDrop(index)}
        onDragEnd={mobile ? undefined : onDragEnd}
        style={{
          ...S.cc,
          opacity: isDragging ? 0.45 : 1,
          outline: isDropTarget ? `2px solid ${BLUE}44` : '2px solid transparent',
          transition: 'opacity 140ms, outline 100ms',
          ...(entry.chart_type === 'power_profile' ? { overflow: 'visible' } : {}),
        }}
      >
        <div
          style={{
            ...S.ccHead,
            ...(mobile
              ? { alignItems: 'flex-start', gap: 8 }
              : {}),
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>

            <div className="brand-english-heading" style={S.ccTitle}>{display.title}</div>
            {entry.chart_type !== 'running_profile' && <div style={{ ...S.ccPeriod, textAlign: isRtl ? 'right' : 'left', direction: 'ltr' }}>{displayDigits(periodCaption)}</div>}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexShrink: 0,
              flexWrap: 'wrap',
              ...(mobile ? { justifyContent: 'flex-end', paddingTop: 0 } : {}),
            }}
          >
            {def && <ChartGuideButton type={entry.chart_type} title={display.title} setting={entry.setting} data={entry.data} />}
            {def?.supportsFullscreen && (
              <button
                type="button"
                className="chart-toolbar-btn"
                onClick={openFullscreen}
                title={t('charts.card.fullscreen')}
                aria-label={t('charts.card.fullscreen')}
                style={S.settingsBtn}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              </button>
            )}
            {def?.hasSettings && (
              <button
                type="button"
                className="chart-toolbar-btn"
                onClick={() => setSettingsOpen(true)}
                title={t('charts.card.settings')}
                aria-label={t('charts.card.settings')}
                style={S.settingsBtn}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            )}
            {!mobile && (
              <span title={t('charts.workspace.dragToReorder', { defaultValue: 'Drag to reorder' })} style={{ color: 'var(--fg-4)', cursor: 'grab', fontSize: 15, lineHeight: 1, padding: '2px 4px', userSelect: 'none' }}>⠿</span>
            )}
            {confirmingRemove ? (
              <div role="group" aria-label={t('charts.card.removeConfirm', { title: display.title })} style={{ display: 'flex', gap: 4 }}>
                <button type="button" className="chart-toolbar-btn" onClick={onRemove} style={{ ...S.removeBtn, width: 'auto', paddingInline: 8, color: 'var(--vo2-pink-500)', fontSize: 10, fontWeight: 800 }}>{t('charts.card.remove')}</button>
                <button type="button" className="chart-toolbar-btn" onClick={() => setConfirmingRemove(false)} style={S.removeBtn} aria-label={t('common.cancel')}>×</button>
              </div>
            ) : (
              <button type="button" className="chart-toolbar-btn" onClick={() => setConfirmingRemove(true)} style={S.removeBtn} title={t('charts.card.remove')} aria-label={t('charts.card.remove')}>×</button>
            )}
          </div>
        </div>
        {def?.legend && <div style={S.ccLeg}>{def.legend(t)}</div>}
        <div style={S.chartBodyBleed}>
          {def
            ? def.render(chartCtx)
            : <div style={{ padding: '20px 0', color: 'var(--fg-4)', fontSize: 12 }}>{t('charts.card.unknownType', { type: entry.chart_type })}</div>
          }
        </div>
      </div>

      {fullscreen && def?.supportsFullscreen && (
        <div className="charts-landscape-fullscreen" style={S.fullscreenOverlay}>
          <div style={S.fullscreenHeader}>
            <div style={{ flex: 1, minWidth: 0 }}>
  
              <div className="brand-english-heading" style={S.ccTitle}>{display.title}</div>
              {entry.chart_type !== 'running_profile' && <div style={{ ...S.ccPeriod, textAlign: isRtl ? 'right' : 'left', direction: 'ltr' }}>{displayDigits(periodCaption)}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <ChartGuideButton type={entry.chart_type} title={display.title} setting={entry.setting} data={entry.data} />
              {def.hasSettings && (
                <button type="button" className="chart-toolbar-btn" onClick={() => setSettingsOpen(true)} title={t('charts.card.settings')} aria-label={t('charts.card.settings')} style={S.settingsBtn}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}
              <button type="button" className="chart-toolbar-btn" onClick={closeFullscreen} style={S.fullscreenClose} title={t('charts.card.closeFullscreen')} aria-label={t('charts.card.closeFullscreen')}>
                ×
              </button>
            </div>
          </div>
          {def.legend && <div style={S.ccLeg}>{def.legend(t)}</div>}
          <div style={S.fullscreenChart}>
            {def.render({ ...chartCtx, fullscreen: true })}
          </div>
        </div>
      )}

      {def?.hasSettings && def.settingsType === 'metrics' && (
        <MetricsChartSettingsPanel
          open={settingsOpen}
          userId={userId}
          setting={entry.setting ?? {}}
          chartTitle={String(entry.setting?.chart_title ?? display.title)}
          saving={savingSettings}
          onClose={() => setSettingsOpen(false)}
          onSave={(patch: MetricsChartSettings) => handleSaveSettings(patch)}
        />
      )}
      {def?.hasSettings && def.settingsType === 'variant' && (
        <ChartVariantSettingsPanel
          open={settingsOpen}
          chartType={entry.chart_type as 'periodic_chart' | 'time_in_zone' | 'power_profile'}
          setting={entry.setting ?? {}}
          chartTitle={display.title}
          saving={savingSettings}
          onClose={() => setSettingsOpen(false)}
          onSave={handleSaveSettings}
        />
      )}
      {def?.hasSettings && (def.settingsType ?? 'pmc') === 'pmc' && (
        <PmcSettingsPanel
          open={settingsOpen}
          setting={entry.setting ?? {}}
          chartTitle={String(entry.setting?.chart_title ?? display.title)}
          saving={savingSettings}
          userId={userId}
          chartId={entry.chart_id}
          defaultStart={recalcDateRange.start}
          defaultEnd={recalcDateRange.end}
          onClose={() => setSettingsOpen(false)}
          onSave={(patch: PmcChartSettings) => handleSaveSettings(patch)}
          onRecalculated={async () => {
            await onChartsRefetch();
            showToast(t('charts.pmcRecalculate.done'));
          }}
          onEditFtp={
            onOpenAthleteSettings
              ? () => {
                  setSettingsOpen(false);
                  onOpenAthleteSettings(userId);
                }
              : undefined
          }
        />
      )}
    </>
  );
}

// ─── ChartLibraryPanel ────────────────────────────────────────────────────────
const KEY_CHART_TYPES = new Set([
  'metrics_chart',
  'pmc_report',
  'periodic_chart',
  'power_profile', 'running_profile',
  'time_in_zone',
  'fitness_summery_report',
]);

function extractUserCharts(response: unknown): UserChartEntry[] {
  const rows = ((response as { data?: { data?: unknown } })?.data?.data ?? []) as UserChartEntry[];
  return rows.filter((chart) => KEY_CHART_TYPES.has(chart.chart_type));
}

function extractAvailableCharts(response: unknown): AvailableChart[] {
  const chartsDict = (response as { data?: { data?: { charts?: Record<string, unknown[]> } } })
    ?.data?.data?.charts ?? {};
  const rows = Object.values(chartsDict)
    .flat()
    .filter((chart): chart is Record<string, unknown> => (
      typeof chart === 'object'
      && chart != null
      && KEY_CHART_TYPES.has(String((chart as Record<string, unknown>).chart_type))
    ))
    .map((chart) => ({
      id: Number(chart.id),
      name: String(chart.en_name ?? chart.name ?? chart.chart_type),
      chart_type: String(chart.chart_type),
    }));

  return Array.from(
    rows.reduce((byType, chart) => {
      if (!byType.has(chart.chart_type)) byType.set(chart.chart_type, chart);
      return byType;
    }, new Map<string, AvailableChart>()).values(),
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const RANGE_VALS: Range[] = [7, 14, 30, 90, 180, 365, 730, -1];

function rangeLabel(t: TFunction, val: Range): string {
  const map: Record<number, [string, string]> = {
    7: ['charts.range7', '7D'],
    14: ['charts.range14', '14D'],
    30: ['charts.range30', '30D'],
    90: ['charts.range90', '90D'],
    180: ['charts.range180', '6M'],
    365: ['charts.range365', '1Y'],
    730: ['charts.range730', '2Y'],
    [-1]: ['charts.rangeCustom', 'Custom'],
  };
  const entry = map[val] ?? map[90];
  return displayDigits(t(entry[0], entry[1]));
}

interface ChartsPageProps {
  user: UserProfile | null;
  /** Effective user ID: athlete ID if a coach selected one, otherwise the logged-in user's ID. */
  effectiveUserId: number | null;
  athletes?: AthleteItem[];
  onAthleteChange?: (id: number) => void;
  onRemoveAthlete?: (id: number, name: string) => void;
  removingAthleteId?: number | null;
  layout?: 'desktop' | 'mobile';
  initialMobileChartType?: string;
  onOpenAthleteSettings?: (userId: number) => void;
}

function chartApiErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data as { message?: string } | undefined;
  return typeof data?.message === 'string' ? data.message : undefined;
}

export function ChartsPage({ user, effectiveUserId, athletes = [], onAthleteChange, onRemoveAthlete, removingAthleteId, layout = 'desktop', initialMobileChartType, onOpenAthleteSettings }: ChartsPageProps) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [storedOrder,setStoredOrder]=useState<string[]>([]);
  const [librarySettingsRequest,setLibrarySettingsRequest]=useState<{chartId:number;token:number}|null>(null);
  const [userCharts, setUserCharts] = useState<UserChartEntry[]>([]);
  const visibleCharts = useMemo(() => uniqueCharts(userCharts), [userCharts]);
  const addingChartRef = useRef(false);
  const [available, setAvailable]   = useState<AvailableChart[]>([]);
  const [loading, setLoading]       = useState(true);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [range, setRange]           = useState<Range>(90);
  const [adding, setAdding]         = useState<number | null>(null);
  const [dragFrom, setDragFrom]     = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const [detailWorkoutId, setDetailWorkoutId] = useState<number | null>(null);
  const [activeInsights, setActiveInsights] = useState<PerformanceInsightType[]>(
    DEFAULT_PERFORMANCE_INSIGHTS,
  );
  const skipNextInsightSave = useRef(false);

  const isMobile = layout === 'mobile';
  const uid = effectiveUserId ?? user?.id ?? null;
  useEffect(()=>{
    setStoredOrder([]);
    if(!uid)return;
    try{const keys=JSON.parse(window.localStorage.getItem(`vo2-chart-layout:${uid}`)??'[]');setStoredOrder(Array.isArray(keys)?keys.filter((key):key is string=>typeof key==='string'):[]);}catch{setStoredOrder([]);}
  },[uid]);

  const athletePicker = onAthleteChange && athletes.length > 0 ? (
    <AthleteSwitcher size="md"
      athletes={athletes}
      selectedId={effectiveUserId}
      onChange={onAthleteChange}
      onOpenSettings={onOpenAthleteSettings}
      onRemoveAthlete={onRemoveAthlete}
      removingAthleteId={removingAthleteId}
    />
  ) : null;
  const openedMobileChart = useRef<string | null>(null);
  const mobileChartRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMobile || !initialMobileChartType || loading) return;
    const requestKey = `${uid}:${initialMobileChartType}`;
    if (openedMobileChart.current === requestKey) return;
    const chart = visibleCharts.find((entry) => entry.chart_type === initialMobileChartType);
    if (!chart) return;
    openedMobileChart.current = requestKey;
    mobileChartRef.current?.querySelector(`[data-chart-id="${chart.chart_id}"]`)?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }, [isMobile, initialMobileChartType, loading, uid, visibleCharts]);

  useEffect(() => {
    if (!uid) return;
    skipNextInsightSave.current = true;
    const stored = window.localStorage.getItem(`vo2-performance-insights:${uid}`);
    if (!stored) {
      setActiveInsights(DEFAULT_PERFORMANCE_INSIGHTS);
      return;
    }
    try {
      const parsed = JSON.parse(stored) as PerformanceInsightType[];
      setActiveInsights(
        parsed.filter((item): item is PerformanceInsightType =>
          SUPPORTED_PERFORMANCE_INSIGHTS.includes(item as PerformanceInsightType),
        ),
      );
    } catch {
      setActiveInsights(DEFAULT_PERFORMANCE_INSIGHTS);
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    if (skipNextInsightSave.current) {
      skipNextInsightSave.current = false;
      return;
    }
    window.localStorage.setItem(`vo2-performance-insights:${uid}`, JSON.stringify(activeInsights));
  }, [activeInsights, uid]);

  const handleWorkoutClick = useCallback((workoutId: number) => {
    if (workoutId > 0) setDetailWorkoutId(workoutId);
  }, []);

  const scrollToRange = useCallback(() => {
    const el = document.getElementById('charts-range-pills')
      ?? document.getElementById('charts-range-controls');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const athleteLabel = useMemo(() => {
    if (effectiveUserId && user?.athletes?.length) {
      return user.athletes.find(a => a.id === effectiveUserId)?.full_name;
    }
    return user?.full_name ?? user?.name;
  }, [effectiveUserId, user]);

  const peakTrendAthletes = useMemo(() => {
    const athletes = user?.athletes ?? [];
    if (!effectiveUserId) return athletes;
    const selected = athletes.find((athlete) => athlete.id === effectiveUserId);
    return selected ? [selected] : [];
  }, [effectiveUserId, user?.athletes]);

  // Custom date range state
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd]     = useState<string>('');

  // Build API params from the current range selection
  const rangeParams = useMemo(() => {
    if (range === -1) {
      // Custom: only send when both dates are filled
      return (customStart && customEnd)
        ? { start_date: customStart, end_date: customEnd }
        : { range: 90 };
    }
    return { range };
  }, [range, customStart, customEnd]);

  const chartRequestId = useRef(0);
  const loadCharts = useCallback(async () => {
    const requestId = ++chartRequestId.current;
    if (!uid) return;
    setLoading(true);
    try {
      const [ucRes, avRes] = await Promise.all([
        chartManager.getUserCharts(uid, rangeParams),
        chartManager.getAvailable(),
      ]);
      if (requestId !== chartRequestId.current) return;
      let uc = extractUserCharts(ucRes);
      const av = extractAvailableCharts(avRes);
      try {
        if (await ensureDefaultMetricsChart(uid, user?.id ?? uid, uc, av)) {
          uc = extractUserCharts(await chartManager.getUserCharts(uid, rangeParams));
        }
      } catch (error) {
        console.warn('Default metric chart could not be enabled', error);
        showToast(t('charts.metricsMonitor.defaultFailed'));
      }
      if (requestId !== chartRequestId.current) return;
      setUserCharts([...uc].sort((a, b) => a.priority - b.priority));
      setAvailable(av);
    } catch (e) {
      if (requestId !== chartRequestId.current) return;
      console.error(e);
    } finally {
      if (requestId === chartRequestId.current) setLoading(false);
    }
  }, [uid, user?.id, rangeParams, showToast, t]);

  useEffect(() => {
    void loadCharts();
  }, [loadCharts]);

  usePageRefresh(loadCharts, [loadCharts]);

  const handleAdd = useCallback(async (chartId: number, setting?: Record<string, unknown>): Promise<boolean> => {
    if (!uid) {
      showToast(t('charts.toast.selectAthlete', 'Select an athlete first'));
      return false;
    }
    if (!chartId) {
      showToast(t('charts.toast.addFailed'));
      return false;
    }

    const template = available.find((c) => c.id === chartId);
    if (!template || addingChartRef.current) return false;
    if (userCharts.some(chart => setting ? chart.chart_type===template.chart_type&&libraryVariantMatches(template.chart_type,chart.setting,setting) : chartIdentity(chart) === chartIdentity({ chart_type: template.chart_type, setting: template.default_setting }))) {
      showToast(t('charts.alreadyPresent'));
      return false;
    }
    const chartName = template.name;
    addingChartRef.current = true;
    setAdding(chartId);
    let created = false;
    try {
      await chartManager.addChart(chartId, uid, setting);
      created = true;
      const res = await chartManager.getUserCharts(uid, rangeParams);
      let uc = extractUserCharts(res);

      const sorted = [...uc].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
      setUserCharts(sorted);
      showToast(
        chartName
          ? t('charts.toast.added', { name: chartName })
          : t('charts.toast.addedGeneric'),
      );
      return true;
    } catch (e) {
      console.error(e);
      if(created){try{const rows=extractUserCharts(await chartManager.getUserCharts(uid,rangeParams));setUserCharts([...rows].sort((a,b)=>a.priority-b.priority));}catch{}}
      const apiMessage = chartApiErrorMessage(e);
      showToast(apiMessage ?? t('charts.toast.addFailed'));
      return false;
    } finally {
      addingChartRef.current = false;
      setAdding(null);
    }
  }, [uid, available, userCharts, rangeParams, showToast, t]);

  const handleAddInsight = useCallback((type: PerformanceInsightType) => {
    setActiveInsights((current) => current.includes(type) ? current : [...current, type]);
  }, []);

  const handleRemoveInsight = useCallback((type: PerformanceInsightType) => {
    setActiveInsights((current) => current.filter((item) => item !== type));
  }, []);

  async function handleRemove(userChartId: number) {
    const target = userCharts.find(chart => chart.chart_id === userChartId);
    const ids = target ? userCharts.filter(chart => chartIdentity(chart) === chartIdentity(target)).map(chart => chart.chart_id) : [userChartId];
    setUserCharts(prev => prev.filter(c => !ids.includes(c.chart_id)));
    try {
      await Promise.all(ids.map(id => chartManager.removeChart(id)));
    } catch (e) {
      console.error(e);
      if (uid) {
        chartManager.getUserCharts(uid, rangeParams).then(res => {
          const uc = extractUserCharts(res);
          setUserCharts([...uc].sort((a, b) => a.priority - b.priority));
        });
      }
    }
  }

  function handleDrop(toIndex: number) {
    if (dragFrom === null || dragFrom === toIndex) return;
    const regular = [...visibleCharts],
      [moved] = regular.splice(dragFrom, 1);
    if (!moved) return;
    regular.splice(toIndex, 0, moved);
    let index = 0;
    const keys = layoutItems.map((item) =>
      item.key.startsWith('chart:')
        ? `chart:${regular[index++].chart_id}`
        : item.key,
    );
    setDragFrom(null);
    setDropTarget(null);
    void saveLayout(keys).catch(() => showToast(t('charts.library.saveFailed')));
  }

  const periodicChartData = useMemo(
    () => userCharts.find((uc) => uc.chart_type === 'periodic_chart')?.data,
    [userCharts],
  );

  const refetchCharts = useCallback(async () => {
    if (!uid) return;
    const ucRes = await chartManager.getUserCharts(uid, rangeParams);
    const uc = extractUserCharts(ucRes);
    setUserCharts([...uc].sort((a, b) => a.priority - b.priority));
  }, [uid, rangeParams]);

  const handleChartSettingSaved = useCallback(async (
    chartId: number,
    setting: Record<string, unknown>,
    chartType: string,
  ) => {
    if (!uid) return;
    setLoading(true);
    try {
      const merged = { ...(userCharts.find((c) => c.chart_id === chartId)?.setting ?? {}), ...setting };
      if (userCharts.some(chart => chart.chart_id !== chartId && chartIdentity(chart) === chartIdentity({ chart_type: chartType, setting: merged }))) throw new Error(t('charts.alreadyPresent'));
      await chartManager.updateChartSettings(chartId, merged);

      if (chartType === 'pmc_report') {
        const pmc = (merged as PmcChartSettings).pmc ?? {};
        const end = range === -1 && customEnd
          ? customEnd
          : new Date().toISOString().slice(0, 10);
        const start = range === -1 && customStart
          ? customStart
          : (() => {
              const d = new Date();
              d.setDate(d.getDate() - (range === -1 ? 90 : range));
              return d.toISOString().slice(0, 10);
            })();

        await chartManager.recalculateTrainingMetrics(chartId, uid, {
          start_date: start,
          end_date: end,
          full_history: true,
          ctl_period: pmc.fitness_CTL_constant ?? 42,
          atl_period: pmc.fatigue_ATL_constant ?? 7,
          initial_ctl: pmc.fitness_CTL_start ?? 0,
          initial_atl: pmc.fatigue_ATL_start ?? 0,
        });
      }

      setUserCharts((prev) =>
        prev.map((c) => (c.chart_id === chartId ? { ...c, setting: merged } : c)),
      );
      await refetchCharts();
    } finally {
      setLoading(false);
    }
  }, [uid, userCharts, range, customStart, customEnd, refetchCharts, t]);

  const recalcDateRange = useMemo(() => {
    const end = range === -1 && customEnd
      ? customEnd
      : new Date().toISOString().slice(0, 10);
    const start = range === -1 && customStart
      ? customStart
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() - (range === -1 ? 90 : range));
          return d.toISOString().slice(0, 10);
        })();
    return { start, end };
  }, [range, customStart, customEnd]);

  const loadingDetail = useMemo(() => {
    if (range === -1 && customStart && customEnd) return `${customStart} → ${customEnd}`;
    return rangeLabel(t, range);
  }, [range, customStart, customEnd, t]);

  const layoutItems: DashboardLibraryItem[] = orderDashboardItems(
    [
      ...activeInsights.map((type) => ({
        key: `insight:${type}`,
        type,
        title: t(`charts.library.types.${type}.title`, {
          defaultValue: t(
            PERFORMANCE_INSIGHT_DEFS.find((def) => def.type === type)?.titleKey ??
              type,
          ),
        }),
      })),
      ...visibleCharts.map((entry) => ({
        key: `chart:${entry.chart_id}`,
        type: entry.chart_type,
        title: resolveChartDisplay(entry, CHART_DEFS[entry.chart_type], t).title,
        setting: entry.setting,
        configurable: [
          'pmc_report',
          'periodic_chart',
          'power_profile',
          'metrics_chart',
        ].includes(entry.chart_type),
      })),
    ],
    storedOrder,
  );
  const saveLayout = async (keys: string[]) => {
    const orderedCharts = keys.flatMap((key) => {
      const entry = visibleCharts.find(
        (chart) => `chart:${chart.chart_id}` === key,
      );
      return entry
        ? userCharts.filter(
            (chart) => chartIdentity(chart) === chartIdentity(entry),
          )
        : [];
    });
    if (orderedCharts.length)
      await chartManager.updatePriority(
        orderedCharts.map((chart, index) => ({ id: chart.chart_id, index })),
      );
    window.localStorage.setItem(`vo2-chart-layout:${uid}`, JSON.stringify(keys));
    setStoredOrder(keys);
    setUserCharts(
      orderedCharts.map((chart, index) => ({ ...chart, priority: index })),
    );
  };
  const configureLibraryItem = (key: string) => {
    setLibraryOpen(false);
    setLibrarySettingsRequest({
      chartId: Number(key.slice(6)),
      token: Date.now(),
    });
  };
  const renderRegular = (
    entry: UserChartEntry,
    index: number,
    mobileView: boolean,
  ) => {
    const chart = entry;
    return mobileView ? (
      <div
        className="mobile-charts-list__item"
        data-chart-id={chart.chart_id}
        key={chart.chart_id}
      >
        <ChartInstance
          entry={chart}
          range={range}
          index={index}
          dragFrom={null}
          dropTarget={null}
          periodicData={periodicChartData}
          onRemove={() => handleRemove(chart.chart_id)}
          onDragStart={() => {}}
          onDragEnter={() => {}}
          onDrop={() => {}}
          onDragEnd={() => {}}
          userId={uid!}
          onSettingSaved={handleChartSettingSaved}
          onWorkoutClick={handleWorkoutClick}
          onScrollToRange={scrollToRange}
          recalcDateRange={recalcDateRange}
          onChartsRefetch={refetchCharts}
          onOpenAthleteSettings={onOpenAthleteSettings}
          mobile
          librarySettingsRequest={librarySettingsRequest}
        />
      </div>
    ) : (
      <ChartInstance
        key={entry.chart_id}
        librarySettingsRequest={librarySettingsRequest}
        entry={entry}
        range={range}
        index={index}
        dragFrom={dragFrom}
        dropTarget={dropTarget}
        periodicData={periodicChartData}
        onRemove={() => handleRemove(entry.chart_id)}
        onDragStart={setDragFrom}
        onDragEnter={setDropTarget}
        onDrop={handleDrop}
        onDragEnd={() => {
          setDragFrom(null);
          setDropTarget(null);
        }}
        userId={uid!}
        onSettingSaved={handleChartSettingSaved}
        onWorkoutClick={handleWorkoutClick}
        onScrollToRange={scrollToRange}
        recalcDateRange={recalcDateRange}
        onChartsRefetch={refetchCharts}
        onOpenAthleteSettings={onOpenAthleteSettings}
      />
    );
  };
  const renderDashboard = (
    cards: Record<string, React.ReactNode>,
    mobileView: boolean,
  ) => (
    <div
      className={mobileView ? 'mobile-charts-list' : 'charts-dashboard-grid'}
      ref={mobileView ? mobileChartRef : undefined}
    >
      {layoutItems.map((item) => (
        <React.Fragment key={item.key}>
          {item.key.startsWith('insight:') ? (
            item.type === 'peak_trends' ? (
              peakTrendAthletes.length ? (
                <DashboardPeakTrends
                  athletes={peakTrendAthletes}
                  dateRange={recalcDateRange}
                  onRemove={() => handleRemoveInsight('peak_trends')}
                />
              ) : null
            ) : (
              cards[item.type]
            )
          ) : (
            (() => {
              const entry = visibleCharts.find(
                (chart) => `chart:${chart.chart_id}` === item.key,
              );
              return entry
                ? renderRegular(entry, visibleCharts.indexOf(entry), mobileView)
                : null;
            })()
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (isMobile) {
    const rangeOptions = RANGE_VALS.map((val) => ({ value: val, label: rangeLabel(t, val) }));

    return (
      <div className="mobile-screen mobile-screen--charts" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="charts-workspace__header">
          <AppHeading className="mobile-charts-page-title">{i18n.language.startsWith("fa") ? "نمودارها" : t("charts.sectionTitle")}</AppHeading><button type="button" onClick={() => setLibraryOpen(true)} style={S.libBtn}><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 7h16M4 17h16M8 4v6M16 14v6"/></svg>{t('charts.library.open')}</button>
          {athletePicker}
        </div>
        <header className="mobile-charts-controls">
          <ChartRangePicker options={rangeOptions} value={range} start={customStart} end={customEnd}
            onApply={(next, start, end) => { setCustomStart(start); setCustomEnd(end); setRange(next); }} />
          
        </header>


        {loading && <NmLoadingOverlay variant="page" message={t('charts.loading')} detail={loadingDetail} />}

        {uid != null && <PerformanceInsightWidgets userId={uid} active={activeInsights} startDate={recalcDateRange.start} endDate={recalcDateRange.end} onRemove={handleRemoveInsight} onWorkoutClick={handleWorkoutClick} renderCards={cards=>renderDashboard(cards,true)}/>}
        {!layoutItems.length&&!loading&&<NmEmptyState actionLabel={t('charts.library.open')} onAction={()=>setLibraryOpen(true)}>{t('charts.emptyTitle')}</NmEmptyState>}

        {libraryOpen && (
          <ChartLibraryPanel
            available={available}
            layoutItems={layoutItems}
            onSaveOrder={saveLayout}
            onConfigure={configureLibraryItem}
            onAdd={handleAdd}
            onAddInsight={handleAddInsight}
          onRemoveInsight={handleRemoveInsight}
            onClose={() => setLibraryOpen(false)}
            adding={adding}
            mobile
          />
        )}

        {detailWorkoutId != null && uid != null && (
          <WorkoutDetailPanel
            workoutId={detailWorkoutId}
            userId={uid}
            onClose={() => setDetailWorkoutId(null)}
            onDeleted={() => setDetailWorkoutId(null)}
            onUpdated={() => void loadCharts()}
          />
        )}
      </div>
    );
  }

  return (
    <div className="charts-workspace" style={S.root}>

      <div className="studio-athlete-metrics"><AthleteMetricsBar userId={uid} athleteName={athleteLabel} /></div>

      {/* Header */}
      {!loading && <div id="charts-range-controls" className="charts-workspace__toolbar" style={S.controls}>
        <div>
          <div style={S.pageTitle}>{t('charts.sectionTitle')}</div>
          <div style={S.pageSub}>{t('charts.pageSubtitle')}</div>
          {athletePicker}
        </div>
        <div style={S.controlsRight}>
          <div className="charts-range-segments" style={S.segTrack}>
            {RANGE_VALS.map((val) => (
              <button key={val} onClick={() => setRange(val)} style={{ ...S.segPill, ...(range === val ? S.segPillActive : {}) }}>
                {rangeLabel(t, val)}
              </button>
            ))}
          </div>
          <button onClick={() => setLibraryOpen(true)} style={S.libBtn}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('charts.library.open')}
          </button>
        </div>
      </div>}

      {/* Custom date range picker — visible only when "Custom" pill is active */}
      {!loading && range === -1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-4)' }}>{t('charts.from')}:</label>
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            style={S.dateInput}
          />
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-4)' }}>{t('charts.to')}:</label>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            style={S.dateInput}
          />
          <span style={{ fontSize: 10, color: 'var(--fg-4)' }}>
            {customStart && customEnd ? `${customStart} → ${customEnd}` : t('charts.pickBothDates')}
          </span>
        </div>
      )}

      <div style={S.contentArea}>
        {loading && (
          <NmLoadingOverlay
            variant="page"
            message={t('charts.loading')}
            detail={loadingDetail}
          />
        )}

        {uid != null && <PerformanceInsightWidgets userId={uid} active={activeInsights} startDate={recalcDateRange.start} endDate={recalcDateRange.end} onRemove={handleRemoveInsight} onWorkoutClick={handleWorkoutClick} renderCards={cards=>renderDashboard(cards,false)}/>}
        {!layoutItems.length&&!loading&&<button type="button" onClick={()=>setLibraryOpen(true)}>{t('charts.library.open')}</button>}
      </div>

      {libraryOpen && (
        <ChartLibraryPanel
          available={available}
          layoutItems={layoutItems}
          onSaveOrder={saveLayout}
          onConfigure={configureLibraryItem}
          onAdd={handleAdd}
          onAddInsight={handleAddInsight}
          onRemoveInsight={handleRemoveInsight}
          onClose={() => setLibraryOpen(false)}
          adding={adding}
        />
      )}

      {detailWorkoutId != null && uid != null && (
        <WorkoutDetailPanel
          workoutId={detailWorkoutId}
          userId={uid}
          onClose={() => setDetailWorkoutId(null)}
          onDeleted={() => setDetailWorkoutId(null)}
          onUpdated={() => void loadCharts()}
        />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', minHeight: '100%' },

  controls:      { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexShrink: 0 },
  controlsRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  pageTitle:     { fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-h3)', color: 'var(--fg-1)', lineHeight: 1.15 },
  pageSub:       { fontSize: 'var(--fs-caption)', color: 'var(--fg-4)', letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase', marginTop: 3 },

  segTrack:      { minHeight: 38, boxSizing: 'border-box', display: 'flex', background: 'var(--surface-sunken)', borderRadius: 12, padding: 3, boxShadow: 'var(--nm-inset-sm)', gap: 2 },
  segPill:       { lineHeight: '20px', padding: '6px 13px', borderRadius: 9, fontSize: 12, fontWeight: 700, color: 'var(--fg-4)', background: 'none', border: 'none', cursor: 'pointer', transition: 'all 170ms', fontFamily: 'var(--font-display)' },
  segPillActive: { background: 'var(--surface-0)', color: 'var(--fg-1)', boxShadow: 'var(--nm-raised-sm)' },

  dateInput: { padding: '6px 10px', borderRadius: 9, border: '1px solid var(--surface-sunken)', background: 'var(--surface-sunken)', color: 'var(--fg-1)', fontSize: 11, fontFamily: 'inherit', outline: 'none' },

  libBtn: { minHeight: 44, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: 'var(--fg-2)', background: 'rgba(128,138,136,.10)', backdropFilter: 'blur(12px)', border: '1px solid var(--nm-border, rgba(128,138,136,.25))', cursor: 'pointer', fontFamily: 'var(--font-body)' },

  contentArea: { position: 'relative', flex: 1, minHeight: 280, display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' },


  cc:        { background: 'var(--surface-0)', borderRadius: 22, padding: '18px 20px', boxShadow: 'var(--nm-raised)', display: 'flex', flexDirection: 'column', gap: 12, position: 'relative', overflow: 'hidden' },
  ccHead:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  ccEye:     { fontSize: 9, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--fg-4)', marginBottom: 3 },
  ccTitle:   { fontSize: 14, fontWeight: 800, letterSpacing: '-.01em', color: 'var(--fg-1)', lineHeight: 1.2 },
  ccPeriod:  { marginTop: 3, fontSize: 12, fontWeight: 600, color: 'var(--fg-2)', letterSpacing: '-.01em', textAlign: 'start' },
  ccLeg:     { display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 10, fontWeight: 600, color: 'var(--fg-3)' },
  chartBodyBleed: { margin: '0 -14px', width: 'calc(100% + 28px)', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  leg:       { display: 'flex', alignItems: 'center', gap: 5 },
  removeBtn: chartToolbarButtonStyle,
  settingsBtn: chartToolbarButtonStyle,

  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '60px 20px', textAlign: 'center' },
  emptyIcon:  { width: 80, height: 80, borderRadius: 24, background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--nm-inset)' },
  emptyTitle: { fontSize: 18, fontWeight: 800, color: 'var(--fg-1)', fontFamily: 'var(--font-display)' },
  emptySub:   { fontSize: 13, color: 'var(--fg-4)', maxWidth: 320, lineHeight: 1.55 },
  emptyBtn:   { marginTop: 8, padding: '10px 24px', borderRadius: 14, border: 'none', cursor: 'pointer', ...primaryActionStyle, fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', boxShadow: primaryActionStyle.boxShadow },

  libPanel:    { position: 'fixed', top: 0, insetInlineEnd: 0, bottom: 0, width: 360, maxWidth: '100vw', zIndex: 1200, background: 'var(--surface-0)', boxShadow: '-8px 0 40px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  libHeader:   { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '24px 20px 16px', borderBottom: '1px solid var(--surface-sunken)', flexShrink: 0 },
  libTitle:    { fontSize: 16, fontWeight: 800, color: 'var(--fg-1)', fontFamily: 'var(--font-display)', marginBottom: 4 },
  libSub:      { fontSize: 11, color: 'var(--fg-4)', lineHeight: 1.4 },
  libClose:    { width: 30, height: 30, borderRadius: 9, border: 'none', cursor: 'pointer', flexShrink: 0, background: 'var(--surface-sunken)', color: 'var(--fg-3)', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--nm-raised-sm)' },
  libSearch:   { width: '100%', padding: '9px 14px', borderRadius: 11, border: '1px solid var(--surface-sunken)', background: 'var(--surface-sunken)', color: 'var(--fg-1)', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  libScroll:   { flex: 1, overflowY: 'auto', padding: '4px 20px 20px' },
  libCatLabel: { fontSize: 9, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--fg-4)', marginBottom: 10 },
  // Keep the action on its own row so translated labels cannot squeeze the copy.
  libCard:     { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', alignItems: 'start', gap: '10px 12px', padding: '14px', borderRadius: 14, background: 'var(--surface-sunken)', marginBottom: 8, boxShadow: 'var(--nm-inset-sm)', textAlign: 'start', minWidth: 0 },
  libCardContent: { minWidth: 0 },
  libCardHeading: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 8px', marginBottom: 6 },
  libCardName: { fontSize: 13, fontWeight: 700, color: 'var(--fg-1)', lineHeight: 1.6, overflowWrap: 'anywhere' },
  libCardDesc: { fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.7, overflowWrap: 'anywhere' },
  libBadge:    { padding: '2px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700, background: 'var(--palette-selected)', color: BLUEL, whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1.6 },
  libAddBtn:   { gridColumn: '1 / -1', justifySelf: 'end', maxWidth: '100%', minHeight: 36, padding: '7px 13px', borderRadius: 10, border: 'none', cursor: 'pointer', ...primaryActionStyle, fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-display)', lineHeight: 1.5, whiteSpace: 'normal', overflowWrap: 'anywhere', boxShadow: primaryActionStyle.boxShadow },
  libAddedBtn: { background: 'var(--surface-0)', color: 'var(--fg-3)', boxShadow: 'none', cursor: 'default' },

  fullscreenOverlay: { position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--surface-0)', display: 'flex', flexDirection: 'column', padding: '20px 24px 16px', gap: 12 },
  fullscreenHeader:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexShrink: 0 },
  fullscreenChart:   { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
  fullscreenClose: chartToolbarButtonStyle,
};
